import { FC, useEffect, useMemo, useState } from 'react';
import { Modal, Button, Divider, Radio } from 'antd';
import { EnvironmentOutlined, CloseOutlined } from '@ant-design/icons';

export type ScopePickerFilterConfig = {
    filter_id: string;
    dimension: string;
    attribute_type: string;
    label: string;
    is_attribute: boolean;
    is_required: boolean;
    depends_on: string[];
    options_path: string;
    order_rank: number;
};

export type ScopePickerConfig = {
    title?: string;
    subtitle?: string;
    feature_name?: string;
    options_source?: string;
    filters?: ScopePickerFilterConfig[];
};

export interface ScopeDropdownModalProps {
    open: boolean;
    onClose: () => void;
    onApply: (selectedValues: Record<string, string>) => void;
    scopeConfig?: ScopePickerConfig;
    optionsRows: Record<string, any>[];
    selectedValues: Record<string, string>;
    applyLoading?: boolean;
    isMandatorySelection?: boolean;
}

export const ScopeDropdownModal: FC<ScopeDropdownModalProps> = ({
    open,
    onClose,
    onApply,
    scopeConfig,
    optionsRows,
    selectedValues,
    applyLoading = false,
    isMandatorySelection = false,
}) => {
    const [tempSelections, setTempSelections] = useState<Record<string, string>>(selectedValues);

    const orderedFilters = useMemo(
        () => [...(scopeConfig?.filters ?? [])].sort((a, b) => a.order_rank - b.order_rank),
        [scopeConfig?.filters],
    );
    const filterIds = orderedFilters.map((filter) => filter.filter_id);

    useEffect(() => {
        if (open) {
            setTempSelections(selectedValues);
        }
    }, [open, selectedValues]);

    const normalizeValue = (rawValue: unknown): string | undefined => {
        if (rawValue === null || rawValue === undefined) {
            return undefined;
        }
        const normalized = String(rawValue).trim();
        return normalized.length > 0 ? normalized : undefined;
    };

    const getNormalizedStringValues = (rawValue: unknown): string[] => {
        if (Array.isArray(rawValue)) {
            return rawValue.flatMap((value) => getNormalizedStringValues(value));
        }
        if (typeof rawValue === 'object' && rawValue !== null) {
            const candidate = rawValue as Record<string, unknown>;
            for (const key of ['label', 'value', 'name', 'attributeDesc', 'attributeExId']) {
                const normalized = normalizeValue(candidate[key]);
                if (normalized) {
                    return [normalized];
                }
            }
            return [];
        }
        const normalized = normalizeValue(rawValue);
        if (!normalized) {
            return [];
        }
        if (normalized.startsWith('[') && normalized.endsWith(']')) {
            try {
                const parsed = JSON.parse(normalized);
                return getNormalizedStringValues(parsed);
            } catch (_error) {
                // Keep normalized value if it is not valid JSON.
            }
        }
        return [normalized];
    };

    const getRowFieldValues = (row: Record<string, any>, filter: ScopePickerFilterConfig): string[] => {
        const candidateKeys = Array.from(
            new Set([
                filter.options_path,
                filter.filter_id,
                filter.options_path?.endsWith('s') ? filter.options_path.slice(0, -1) : `${filter.options_path}s`,
                filter.filter_id?.endsWith('s') ? filter.filter_id.slice(0, -1) : `${filter.filter_id}s`,
            ]),
        ).filter(Boolean);

        for (const key of candidateKeys) {
            const values = getNormalizedStringValues(row?.[key]);
            if (values.length > 0) {
                return values;
            }
        }
        return [];
    };

    const getFilterOptions = (filter: ScopePickerFilterConfig) => {
        if (!filter.depends_on?.length) {
            return Array.from(new Set(optionsRows.flatMap((row) => getRowFieldValues(row, filter))));
        }

        let filteredRows = optionsRows;
        for (const parentFilterId of filter.depends_on) {
            const parentFilter = orderedFilters.find((item) => item.filter_id === parentFilterId);
            const parentSelection = normalizeValue(tempSelections[parentFilterId]);
            if (!parentFilter || !parentSelection) {
                return [];
            }
            const normalizedParentSelection = parentSelection.toLowerCase();
            filteredRows = filteredRows.filter((row) =>
                getRowFieldValues(row, parentFilter).some((value) => value.toLowerCase() === normalizedParentSelection),
            );
        }

        return Array.from(new Set(filteredRows.flatMap((row) => getRowFieldValues(row, filter))));
    };

    const updateSelection = (filter: ScopePickerFilterConfig, value?: string) => {
        setTempSelections((prev) => {
            const next = { ...prev };
            if (!value) {
                delete next[filter.filter_id];
            } else {
                next[filter.filter_id] = value;
            }
            const filtersToClear = new Set<string>();
            const queue = [filter.filter_id];
            while (queue.length > 0) {
                const currentFilterId = queue.shift();
                if (!currentFilterId) {
                    continue;
                }
                orderedFilters.forEach((candidate) => {
                    if (candidate.depends_on?.includes(currentFilterId) && !filtersToClear.has(candidate.filter_id)) {
                        filtersToClear.add(candidate.filter_id);
                        queue.push(candidate.filter_id);
                    }
                });
            }
            filtersToClear.forEach((filterId) => {
                delete next[filterId];
            });
            return next;
        });
    };

    const hasValue = (value?: string) => typeof value === 'string' && value.trim().length > 0;

    const hasExistingScope = filterIds.some((filterId) => hasValue(selectedValues[filterId]));
    const hasScopeChanged = filterIds.some((filterId) => (tempSelections[filterId] ?? '') !== (selectedValues[filterId] ?? ''));
    const requiredFilterIds = orderedFilters.filter((filter) => filter.is_required).map((filter) => filter.filter_id);
    const hasAllSelections = requiredFilterIds.every((filterId) => hasValue(tempSelections[filterId]));
    const isApplyDisabled = !hasAllSelections || !hasScopeChanged;
    const applyLabel = hasExistingScope ? 'Save Scope' : 'Apply Scope';

    return (
        <Modal
            open={open}
            onCancel={isMandatorySelection ? undefined : onClose}
            footer={null}
            closable={false}
            maskClosable={false}
            keyboard={!isMandatorySelection}
            width={568}
            centered
            zIndex={1100}
            getContainer={() => document.body}
            className="scope-dropdown-modal"
            bodyStyle={{ padding: 0 }}
            destroyOnClose
        >
            <div className="flex flex-col gap-0 p-6 bg-white rounded-3xl">
                <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1">
                            <EnvironmentOutlined className="text-xl text-headerColor" />
                            <h3 className="text-xl font-bold text-headerColor leading-8 m-0">
                                {scopeConfig?.title || 'Select your scope'}
                            </h3>
                        </div>
                        <p className="text-base text-promoColour leading-[22px]">
                            {scopeConfig?.subtitle ||
                                "Pick a category and (optionally) a market - we'll use these as your default global filter on every page."}
                        </p>
                    </div>
                    {!isMandatorySelection ? (
                        <CloseOutlined color="grey" className="cursor-pointer" onClick={onClose} />
                    ) : null}
                </div>

                <Divider className="!my-[16px]" />

                <div className="grid min-h-[220px] grid-cols-1 gap-4 pb-4 md:grid-cols-2">
                    {orderedFilters
                        .filter((filter) => {
                            return (filter.depends_on ?? []).every((dependsOnFilterId) =>
                                hasValue(tempSelections[dependsOnFilterId]),
                            );
                        })
                        .map((filter, index) => {
                        const options = getFilterOptions(filter);
                        const disabled = (filter.depends_on ?? []).some((dependsOnFilterId) => !hasValue(tempSelections[dependsOnFilterId]));
                        const isScrollableList = options.length > 6;

                        return (
                            <div
                                key={filter.filter_id}
                                className={`flex min-w-0 flex-col gap-2 ${index > 0 ? 'md:border-l md:border-[#f0f0f0] md:pl-4' : ''}`}
                            >
                                <p className="font-semibold text-base text-promoColour uppercase">{filter.label}</p>
                                <Radio.Group
                                    value={tempSelections[filter.filter_id]}
                                    onChange={(event) => updateSelection(filter, event.target.value)}
                                    className={`flex flex-col gap-2 [&_.ant-radio-wrapper]:block ${
                                        isScrollableList ? 'max-h-48 overflow-auto' : ''
                                    }`}
                                    disabled={disabled}
                                >
                                    {options.map((option) => (
                                        <Radio key={option} value={option} className="font-normal !text-base">
                                            {option}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            </div>
                        );
                    })}
                </div>

                <Divider className="!my-[16px]" />

                <div className="flex gap-6 items-center justify-end">
                    {!isMandatorySelection ? (
                        <Button
                            onClick={onClose}
                            type="default"
                            style={{ borderRadius: 6 }}
                            className="!h-8 !rounded-[8px] !border-[#d9d9d9] !bg-white !px-4 !text-black hover:!border-[#d9d9d9] hover:!bg-white"
                        >
                            Cancel
                        </Button>
                    ) : null}
                    <Button
                        type="primary"
                        onClick={() => onApply(tempSelections)}
                        loading={applyLoading}
                        disabled={isApplyDisabled}
                        style={{ borderRadius: 6 }}
                    >
                        {applyLabel}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default ScopeDropdownModal;
